import { Card } from '../../types';

/**
 * Pure helpers for the compact (mobile) hand. Kept framework-free so the
 * grouping / ordering / positioning rules can be unit tested directly.
 */

export type HandCard = Card & { disabled?: boolean };

/** Alternating black / red so neighbouring groups are easy to tell apart. */
export const SUIT_ORDER: Card['suite'][] = [
  'spades',
  'hearts',
  'clubs',
  'diamonds',
];

export const SUIT_TITLE: Record<Card['suite'], string> = {
  spades: 'Spades',
  hearts: 'Hearts',
  clubs: 'Clubs',
  diamonds: 'Diamonds',
};

export const SUIT_GLYPH: Record<Card['suite'], string> = {
  spades: '♠',
  hearts: '♥',
  clubs: '♣',
  diamonds: '♦',
};

const RANK_ORDER = [
  'A',
  'K',
  'Q',
  'J',
  '10',
  '9',
  '8',
  '7',
  '6',
  '5',
  '4',
  '3',
  '2',
];

const rankIndex = (value: string) => {
  const i = RANK_ORDER.indexOf(value.trim().toUpperCase());
  return i === -1 ? RANK_ORDER.length : i;
};

export interface SuitGroup<T extends HandCard = HandCard> {
  suite: Card['suite'];
  cards: { card: T; index: number }[];
  playable: number;
}

/**
 * Groups the hand by suit (fixed suit order, high → low rank inside a suit).
 * `index` is the card's position in the original array so callers can keep
 * using it for keys / test ids. Empty suits are omitted.
 */
export const groupHandBySuit = <T extends HandCard>(
  cards: T[]
): SuitGroup<T>[] =>
  SUIT_ORDER.map(suite => {
    const inSuit = cards
      .map((card, index) => ({ card, index }))
      .filter(({ card }) => card.suite === suite)
      .sort(
        (a, b) =>
          rankIndex(a.card.value) - rankIndex(b.card.value) || a.index - b.index
      );
    return {
      suite,
      cards: inSuit,
      playable: inSuit.filter(({ card }) => !card.disabled).length,
    };
  }).filter(group => group.cards.length > 0);

export const cardKey = (card: Card) => `${card.suite}-${card.value.trim()}`;

/**
 * Keeps a raised (confirm) card fully on screen. A card tapped while partly
 * scrolled out of the hand strip reports a left edge outside the viewport.
 */
export const clampCardLeft = (
  left: number,
  cardWidth: number,
  viewportWidth: number,
  margin = 8
) => {
  const max = viewportWidth - cardWidth - margin;
  if (max <= margin) return Math.max(0, left);
  return Math.min(Math.max(left, margin), max);
};
